import { http, setToken } from './http';

/**
 * Papéis da plataforma. `require_platform` no backend admite os três
 * primeiros; SUPPLIER autentica mas não alcança nada no clearance.
 */
export type UserRole = 'admin' | 'employee' | 'buyer' | 'supplier';

/** Os papéis que o backend aceita nas rotas do clearance (`require_platform`). */
export const PLATFORM_ROLES: readonly UserRole[] = ['admin', 'employee', 'buyer'];

export interface AuthUser {
  id: string;
  /**
   * **Opcional.** O buyer entra por telefone e pode não ter e-mail — era o que
   * impedia criar conta para a base de clientes, que veio toda do WhatsApp.
   */
  email?: string | null;
  phone?: string | null;
  name: string;
  /**
   * Eixo único de autorização. `null` só aparece em documento legado sem
   * papel, que o backend recusa no login e na sessão — aqui é tratado como
   * "sem acesso", nunca como permissão.
   */
  role?: UserRole | null;
  customerId?: string | null;
  supplierId?: string | null;
  /**
   * Ainda falta aceitar os termos vigentes. Quem decide é o servidor
   * (`core/terms.py`): só buyer, e só enquanto a versão aceita não for a atual.
   * Enquanto `true`, a vitrine fica atrás do wizard de boas-vindas.
   */
  termsPending?: boolean;
  /** Versão vigente dos termos — a que o aceite envia de volta. */
  termsVersion?: string;
  /** `user_type` legado do Engage (`admin` | `company_admin`). NÃO é papel. */
  type: 'admin' | string;
}

/** Diz se o papel alcança o clearance — espelha `require_platform`. */
export function hasPlatformAccess(user: AuthUser | null): boolean {
  if (!user?.role) return false;
  return PLATFORM_ROLES.includes(user.role);
}

export interface AuthSession {
  user: AuthUser;
  token: string;
  expiresAt: string;
}

export interface LoginPayload {
  /** E-mail **ou** telefone. O backend resolve pelo `@`. */
  identifier: string;
  password: string;
}

export const authService = {
  async login(payload: LoginPayload): Promise<AuthSession> {
    // `auth: false` aqui é load-bearing, não sobra do ticket 6: este é o único
    // request cujo 401 quer dizer "credencial errada", e não "sessão expirada".
    // Sem o flag, `handleUnauthorized` recarregaria a própria tela de login a
    // cada tentativa malsucedida, engolindo a mensagem de erro.
    const session = await http.post<AuthSession>('/auth/admin/login', payload, {
      auth: false,
    });
    setToken(session.token);
    return session;
  },

  async session(): Promise<AuthSession | null> {
    try {
      return await http.get<AuthSession | null>('/auth/session');
    } catch {
      return null;
    }
  },

  async logout(): Promise<void> {
    try {
      await http.post<void>('/auth/logout');
    } finally {
      setToken(null);
    }
  },
};
